import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Settings, Plus, Trash2, AlertCircle, ChevronUp, ChevronDown, Star, Pencil, KeyRound, X, Mail, UserCog } from 'lucide-react';
import {
  business, adminUsers, type Directorate, type Department, type Budget, type User, type Role,
  type Workflow, type WorkflowStep, type StepType, type FinanceApprover, type Business,
} from '../../lib/api';
import { useAuth } from '../../lib/auth';
type Tab = 'directorates' | 'departments' | 'budgets' | 'users' | 'workflows' | 'finance';

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: 'employee', label: 'Employee' },
  { value: 'head_of_dept', label: 'Head of Department' },
  { value: 'director', label: 'Director' },
  { value: 'finance', label: 'Finance' },
  { value: 'ceo', label: 'CEO' },
];

export default function BusinessSetup() {
  const { user: actor } = useAuth();
  const [params] = useSearchParams();
  const businessId = params.get('business_id') || undefined;
  const [tab, setTab] = useState<Tab>('directorates');

  const [directorates, setDirectorates] = useState<Directorate[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);
  const [financeApprovers, setFinanceApprovers] = useState<FinanceApprover[]>([]);
  const [currentBusiness, setCurrentBusiness] = useState<Business | null>(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [d, dept, b, u, w, fa, del] = await Promise.all([
        business.directorates.list(businessId),
        business.departments.list(businessId),
        business.budgets.list(year, businessId),
        adminUsers.list(undefined, businessId),
        business.workflows.list(businessId),
        business.financeApprovers.list(businessId),
        business.getDelegate(businessId),
      ]);
      setDirectorates(d.directorates);
      setDepartments(dept.departments);
      setBudgets(b.budgets);
      setUsers(u.users);
      setWorkflows(w.workflows);
      setWorkflowSteps(w.steps);
      setFinanceApprovers(fa.financeApprovers);
      setCurrentBusiness(del.business);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [businessId, year]);

  useEffect(() => { load(); }, [load]);

  const directorateName = (id: string) => directorates.find(d => d.id === id)?.name || '—';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="w-6 h-6 text-green-600" /> Business Setup
        </h1>
        <p className="text-gray-500 text-sm mt-0.5">Org structure, budgets, and people</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="flex gap-2 border-b border-gray-200">
        {(['directorates', 'departments', 'budgets', 'users', 'workflows', 'finance'] as Tab[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 -mb-px transition-colors ${
              tab === t ? 'border-green-600 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto" /></div>
      ) : (
        <>
          {tab === 'directorates' && (
            <DirectoratesTab directorates={directorates} users={users} businessId={businessId} onChange={load} />
          )}
          {tab === 'departments' && (
            <DepartmentsTab departments={departments} directorates={directorates} users={users} workflows={workflows}
              businessId={businessId} onChange={load} directorateName={directorateName} />
          )}
          {tab === 'budgets' && (
            <BudgetsTab budgets={budgets} departments={departments} businessId={businessId} year={year} setYear={setYear} onChange={load} />
          )}
          {tab === 'users' && (
            <UsersTab users={users} departments={departments} directorates={directorates} businessId={businessId} onChange={load}
              isPlatformAdminActor={actor?.role === 'platform_admin'} />
          )}
          {tab === 'workflows' && (
            <WorkflowsTab workflows={workflows} steps={workflowSteps} users={users} businessId={businessId} onChange={load} />
          )}
          {tab === 'finance' && (
            <FinanceTab financeApprovers={financeApprovers} users={users} currentBusiness={currentBusiness}
              businessId={businessId} onChange={load} />
          )}
        </>
      )}
    </div>
  );
}

function DirectoratesTab({ directorates, users, businessId, onChange }: {
  directorates: Directorate[]; users: User[]; businessId?: string; onChange: () => void;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await business.directorates.create(name, businessId);
      setName('');
      onChange();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const setDirector = async (id: string, directorUserId: string) => {
    await business.directorates.update(id, { director_user_id: directorUserId || null }, businessId);
    onChange();
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this directorate? Its departments will also be deleted.')) return;
    await business.directorates.remove(id, businessId);
    onChange();
  };

  const directors = users.filter(u => u.role === 'director');

  return (
    <div className="space-y-4">
      <form onSubmit={create} className="flex gap-2">
        <input className="input-field" placeholder="New directorate name" value={name} onChange={e => setName(e.target.value)} required />
        <button type="submit" className="btn-primary flex items-center gap-2 flex-shrink-0"><Plus className="w-4 h-4" /> Add</button>
      </form>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
      <div className="card overflow-hidden p-0">
        {directorates.length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">No directorates yet</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {directorates.map(d => (
              <div key={d.id} className="flex items-center gap-3 px-4 py-3">
                <span className="flex-1 font-medium text-gray-900">{d.name}</span>
                <select
                  className="input-field w-56 text-sm py-1.5"
                  value={d.director_user_id || ''}
                  onChange={e => setDirector(d.id, e.target.value)}
                >
                  <option value="">No director assigned</option>
                  {directors.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                </select>
                <button onClick={() => remove(d.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DepartmentsTab({ departments, directorates, users, workflows, businessId, onChange, directorateName }: {
  departments: Department[]; directorates: Directorate[]; users: User[]; workflows: Workflow[]; businessId?: string; onChange: () => void;
  directorateName: (id: string) => string;
}) {
  const [name, setName] = useState('');
  const [directorateId, setDirectorateId] = useState('');
  const [error, setError] = useState('');

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!directorateId) { setError('Choose a directorate'); return; }
    try {
      await business.departments.create(name, directorateId, businessId);
      setName('');
      onChange();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const setHead = async (id: string, headUserId: string) => {
    await business.departments.update(id, { head_user_id: headUserId || null }, businessId);
    onChange();
  };

  const setWorkflow = async (id: string, workflowId: string) => {
    await business.departments.update(id, { approval_workflow_id: workflowId || null }, businessId);
    onChange();
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this department?')) return;
    await business.departments.remove(id, businessId);
    onChange();
  };

  const heads = users.filter(u => u.role === 'head_of_dept');

  return (
    <div className="space-y-4">
      <form onSubmit={create} className="flex gap-2 flex-wrap">
        <input className="input-field flex-1 min-w-[160px]" placeholder="New department name" value={name} onChange={e => setName(e.target.value)} required />
        <select className="input-field w-52" value={directorateId} onChange={e => setDirectorateId(e.target.value)} required>
          <option value="">Directorate...</option>
          {directorates.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <button type="submit" className="btn-primary flex items-center gap-2 flex-shrink-0"><Plus className="w-4 h-4" /> Add</button>
      </form>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
      <div className="card overflow-hidden p-0">
        {departments.length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">No departments yet</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {departments.map(d => (
              <div key={d.id} className="flex items-center gap-3 px-4 py-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">{d.name}</div>
                  <div className="text-xs text-gray-400">{directorateName(d.directorate_id)}</div>
                </div>
                <select
                  className="input-field w-56 text-sm py-1.5"
                  value={d.head_user_id || ''}
                  onChange={e => setHead(d.id, e.target.value)}
                >
                  <option value="">No head assigned</option>
                  {heads.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                </select>
                <select
                  className="input-field w-56 text-sm py-1.5"
                  value={d.approval_workflow_id || ''}
                  onChange={e => setWorkflow(d.id, e.target.value)}
                  title="Purchase request approval workflow"
                >
                  <option value="">Use business default workflow</option>
                  {workflows.map(w => <option key={w.id} value={w.id}>{w.name}{w.is_default ? ' (default)' : ''}</option>)}
                </select>
                <button onClick={() => remove(d.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BudgetsTab({ budgets, departments, businessId, year, setYear, onChange }: {
  budgets: Budget[]; departments: Department[]; businessId?: string; year: number; setYear: (y: number) => void; onChange: () => void;
}) {
  const [departmentId, setDepartmentId] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!departmentId || !amount) return;
    try {
      await business.budgets.create({ department_id: departmentId, fiscal_year: year, amount_allocated: parseFloat(amount) }, businessId);
      setDepartmentId(''); setAmount('');
      onChange();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const update = async (id: string, newAmount: string) => {
    const parsed = parseFloat(newAmount);
    if (!parsed || parsed <= 0) return;
    await business.budgets.update(id, parsed, businessId);
    onChange();
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this budget?')) return;
    await business.budgets.remove(id, businessId);
    onChange();
  };

  const departmentsWithoutBudget = departments.filter(d => !budgets.some(b => b.department_id === d.id));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="label mb-0">Fiscal year</label>
        <input type="number" className="input-field w-28" value={year} onChange={e => setYear(Number(e.target.value))} />
      </div>

      {departmentsWithoutBudget.length > 0 && (
        <form onSubmit={create} className="flex gap-2 flex-wrap">
          <select className="input-field w-52" value={departmentId} onChange={e => setDepartmentId(e.target.value)} required>
            <option value="">Department...</option>
            {departmentsWithoutBudget.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <input type="number" step="0.01" min="0.01" className="input-field w-40" placeholder="Allocated amount" value={amount} onChange={e => setAmount(e.target.value)} required />
          <button type="submit" className="btn-primary flex items-center gap-2 flex-shrink-0"><Plus className="w-4 h-4" /> Add Budget</button>
        </form>
      )}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

      <div className="card overflow-hidden p-0">
        {budgets.length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">No budgets set for {year}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {budgets.map(b => {
              const pct = b.amount_allocated > 0 ? Math.min(100, Math.round((b.amount_spent / b.amount_allocated) * 100)) : 0;
              return (
                <div key={b.id} className="px-4 py-3 space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="flex-1 font-medium text-gray-900">{b.department_name}</span>
                    <span className="text-sm tabular-nums text-gray-500">{b.amount_spent.toFixed(2)} / </span>
                    <input
                      type="number"
                      step="0.01"
                      defaultValue={b.amount_allocated}
                      onBlur={e => update(b.id, e.target.value)}
                      className="input-field w-28 text-sm py-1.5"
                    />
                    <button onClick={() => remove(b.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function UsersTab({ users, departments, directorates, businessId, onChange, isPlatformAdminActor }: {
  users: User[]; departments: Department[]; directorates: Directorate[]; businessId?: string; onChange: () => void;
  isPlatformAdminActor: boolean;
}) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<Role>('employee');
  const [departmentId, setDepartmentId] = useState('');
  const [directorateId, setDirectorateId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await adminUsers.create({
        email, full_name: fullName, role,
        department_id: (role === 'employee' || role === 'head_of_dept') ? departmentId : undefined,
        directorate_id: role === 'director' ? directorateId : undefined,
        business_id: businessId,
      });
      setEmail(''); setFullName(''); setDepartmentId(''); setDirectorateId('');
      onChange();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (u: User) => {
    await adminUsers.update(u.id, { is_active: (u.is_active ? 0 : 1) as any });
    onChange();
  };

  const departmentName = (id: string | null) => departments.find(d => d.id === id)?.name;
  const directorateNameFor = (id: string | null) => directorates.find(d => d.id === id)?.name;

  return (
    <div className="space-y-4">
      <form onSubmit={create} className="card space-y-3">
        <h3 className="font-semibold text-gray-900 text-sm">Add a user</h3>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input className="input-field" placeholder="Full name" value={fullName} onChange={e => setFullName(e.target.value)} />
          <input type="email" className="input-field" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required />
          <select className="input-field" value={role} onChange={e => setRole(e.target.value as Role)}>
            {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {(role === 'employee' || role === 'head_of_dept') && (
            <select className="input-field" value={departmentId} onChange={e => setDepartmentId(e.target.value)} required>
              <option value="">Department...</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          {role === 'director' && (
            <select className="input-field" value={directorateId} onChange={e => setDirectorateId(e.target.value)} required>
              <option value="">Directorate...</option>
              {directorates.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
        </div>
        <p className="text-xs text-gray-500">They'll receive an email to set their password.</p>
        <button type="submit" className="btn-primary flex items-center gap-2" disabled={loading}>
          <Plus className="w-4 h-4" /> {loading ? 'Adding...' : 'Add User'}
        </button>
      </form>

      <div className="card overflow-hidden p-0">
        {users.length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">No users yet</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {users.map(u => (
              <UserRow
                key={u.id}
                user={u}
                departments={departments}
                directorates={directorates}
                departmentName={departmentName}
                directorateNameFor={directorateNameFor}
                isPlatformAdminActor={isPlatformAdminActor}
                onToggleActive={() => toggleActive(u)}
                onChange={onChange}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UserRow({ user, departments, directorates, departmentName, directorateNameFor, isPlatformAdminActor, onToggleActive, onChange }: {
  user: User; departments: Department[]; directorates: Directorate[];
  departmentName: (id: string | null) => string | undefined; directorateNameFor: (id: string | null) => string | undefined;
  isPlatformAdminActor: boolean; onToggleActive: () => void; onChange: () => void;
}) {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [editing, setEditing] = useState(false);
  const [role, setRole] = useState<Role>(user.role);
  const [email, setEmail] = useState(user.email);
  const [departmentId, setDepartmentId] = useState(user.department_id || '');
  const [directorateId, setDirectorateId] = useState(user.directorate_id || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newPassword, setNewPassword] = useState<string | null>(null);
  const [inviteMsg, setInviteMsg] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  const roleOptions = isPlatformAdminActor ? [...ROLE_OPTIONS, { value: 'platform_admin' as Role, label: 'Platform Admin' }] : ROLE_OPTIONS;

  const startEdit = () => {
    setRole(user.role);
    setEmail(user.email);
    setDepartmentId(user.department_id || '');
    setDirectorateId(user.directorate_id || '');
    setError('');
    setEditing(true);
  };

  const saveRole = async () => {
    setError('');
    if (!email.trim()) { setError('Email is required'); return; }
    if ((role === 'employee' || role === 'head_of_dept') && !departmentId) { setError('Choose a department'); return; }
    if (role === 'director' && !directorateId) { setError('Choose a directorate'); return; }
    setSaving(true);
    try {
      await adminUsers.update(user.id, {
        role, email: email.trim(),
        department_id: (role === 'employee' || role === 'head_of_dept') ? departmentId : null,
        directorate_id: role === 'director' ? directorateId : null,
      });
      setEditing(false);
      onChange();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const impersonate = async () => {
    if (!confirm(`Log in as ${user.full_name || user.email}? You can return to your own account from the banner at the top.`)) return;
    setImpersonateLoading(true);
    try {
      await adminUsers.impersonate(user.id);
      await refresh();
      navigate('/');
    } catch (err: any) {
      alert(err.message);
    } finally {
      setImpersonateLoading(false);
    }
  };

  const remove = async () => {
    if (!confirm(`Remove ${user.full_name || user.email}? This cannot be undone.`)) return;
    try {
      await adminUsers.remove(user.id);
      onChange();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const generatePassword = async () => {
    if (!confirm(`Generate a new password for ${user.full_name || user.email}? Their old password will stop working.`)) return;
    const generated = crypto.randomUUID().replace(/-/g, '').slice(0, 12);
    try {
      await adminUsers.resetPassword(user.id, generated);
      setNewPassword(generated);
    } catch (err: any) {
      alert(err.message);
    }
  };

  const resendInvite = async () => {
    setInviteLoading(true);
    setInviteMsg(null);
    try {
      const { message } = await adminUsers.resendInvite(user.id);
      setInviteMsg(message);
    } catch (err: any) {
      setInviteMsg(err.message);
    } finally {
      setInviteLoading(false);
    }
  };

  if (editing) {
    return (
      <div className="px-4 py-3 space-y-2 bg-gray-50">
        <div className="font-medium text-gray-900 text-sm">{user.full_name || user.email}</div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-xs">{error}</div>}
        <div className="flex gap-2 flex-wrap items-center">
          <input type="email" className="input-field w-56 text-sm py-1.5" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" />
          <select className="input-field w-44 text-sm py-1.5" value={role} onChange={e => setRole(e.target.value as Role)}>
            {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {(role === 'employee' || role === 'head_of_dept') && (
            <select className="input-field w-48 text-sm py-1.5" value={departmentId} onChange={e => setDepartmentId(e.target.value)}>
              <option value="">Department...</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          {role === 'director' && (
            <select className="input-field w-48 text-sm py-1.5" value={directorateId} onChange={e => setDirectorateId(e.target.value)}>
              <option value="">Directorate...</option>
              {directorates.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          <button onClick={saveRole} className="btn-primary text-xs py-1.5 px-3" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button onClick={() => setEditing(false)} className="p-1.5 text-gray-400 hover:text-gray-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 space-y-2">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 truncate">{user.full_name || '(no name)'}</div>
          <div className="text-xs text-gray-400 truncate">
            {user.email}
            {' · '}{ROLE_OPTIONS.find(r => r.value === user.role)?.label || user.role}
            {departmentName(user.department_id) ? ` · ${departmentName(user.department_id)}` : ''}
            {directorateNameFor(user.directorate_id) ? ` · ${directorateNameFor(user.directorate_id)}` : ''}
          </div>
        </div>
        {!user.is_active && <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0">Disabled</span>}
        {isPlatformAdminActor && user.role !== 'platform_admin' && user.is_active && (
          <button onClick={impersonate} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg flex-shrink-0" title="Log in as this user" disabled={impersonateLoading}>
            <UserCog className="w-4 h-4" />
          </button>
        )}
        <button onClick={startEdit} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg flex-shrink-0" title="Change role / email">
          <Pencil className="w-4 h-4" />
        </button>
        <button onClick={resendInvite} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg flex-shrink-0" title="Resend invite email" disabled={inviteLoading}>
          <Mail className="w-4 h-4" />
        </button>
        <button onClick={generatePassword} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg flex-shrink-0" title="Generate new password">
          <KeyRound className="w-4 h-4" />
        </button>
        <button onClick={onToggleActive} className="btn-secondary text-xs py-1 px-3 flex-shrink-0">
          {user.is_active ? 'Disable' : 'Enable'}
        </button>
        <button onClick={remove} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0" title="Remove user">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {newPassword && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg text-xs flex items-center justify-between gap-2">
          <span>New password: <code className="font-mono font-semibold">{newPassword}</code> — share this with them directly.</span>
          <button onClick={() => setNewPassword(null)} className="text-green-700 hover:text-green-900"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      {inviteMsg && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg text-xs flex items-center justify-between gap-2">
          <span>{inviteMsg}</span>
          <button onClick={() => setInviteMsg(null)} className="text-green-700 hover:text-green-900"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
    </div>
  );
}

function WorkflowsTab({ workflows, steps, users, businessId, onChange }: {
  workflows: Workflow[]; steps: WorkflowStep[]; users: User[]; businessId?: string; onChange: () => void;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await business.workflows.create(name, businessId);
      setName('');
      onChange();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const setDefault = async (id: string) => {
    try {
      await business.workflows.update(id, { is_default: true }, businessId);
      onChange();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const rename = async (id: string, newName: string) => {
    if (!newName.trim()) return;
    await business.workflows.update(id, { name: newName }, businessId);
    onChange();
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this workflow?')) return;
    try {
      await business.workflows.remove(id, businessId);
      onChange();
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Workflows control who has to approve a Purchase Request before an employee can buy something. The
        default workflow is a simple submitter &rarr; line manager chain; build a custom one here for
        departments that need more sign-off, then assign it on the Departments tab.
      </p>
      <form onSubmit={create} className="flex gap-2">
        <input className="input-field" placeholder="New workflow name" value={name} onChange={e => setName(e.target.value)} required />
        <button type="submit" className="btn-primary flex items-center gap-2 flex-shrink-0"><Plus className="w-4 h-4" /> Add</button>
      </form>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
      {workflows.length === 0 ? (
        <div className="card text-center text-gray-400 text-sm py-6">No workflows yet</div>
      ) : (
        workflows.map(w => (
          <WorkflowCard
            key={w.id}
            workflow={w}
            steps={steps.filter(s => s.workflow_id === w.id).sort((a, b) => a.step_order - b.step_order)}
            users={users}
            businessId={businessId}
            onChange={onChange}
            onSetDefault={() => setDefault(w.id)}
            onRename={n => rename(w.id, n)}
            onRemove={() => remove(w.id)}
            setError={setError}
          />
        ))
      )}
    </div>
  );
}

function WorkflowCard({ workflow, steps, users, businessId, onChange, onSetDefault, onRename, onRemove, setError }: {
  workflow: Workflow; steps: WorkflowStep[]; users: User[]; businessId?: string; onChange: () => void;
  onSetDefault: () => void; onRename: (name: string) => void; onRemove: () => void; setError: (message: string) => void;
}) {
  const [stepType, setStepType] = useState<StepType>('line_manager');
  const [specificUserId, setSpecificUserId] = useState('');

  const addStep = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await business.workflows.addStep(
        workflow.id,
        { step_type: stepType, specific_user_id: stepType === 'specific_user' ? specificUserId : undefined },
        businessId,
      );
      setSpecificUserId('');
      onChange();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const removeStep = async (stepId: string) => {
    await business.workflows.removeStep(workflow.id, stepId, businessId);
    onChange();
  };

  const moveStep = async (stepId: string, direction: 'up' | 'down') => {
    try {
      await business.workflows.moveStep(workflow.id, stepId, direction, businessId);
      onChange();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const stepLabel = (s: WorkflowStep) => {
    if (s.step_type === 'line_manager') return 'Line Manager';
    const person = users.find(u => u.id === s.specific_user_id);
    return person ? (person.full_name || person.email) : 'Unknown person';
  };

  const lineManagerStepCount = steps.filter(s => s.step_type === 'line_manager').length;

  return (
    <div className="card space-y-3">
      <div className="flex items-center gap-3">
        <input
          className="input-field flex-1 font-medium"
          defaultValue={workflow.name}
          onBlur={e => e.target.value !== workflow.name && onRename(e.target.value)}
        />
        <button
          onClick={onSetDefault}
          disabled={!!workflow.is_default}
          className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-full flex-shrink-0 ${
            workflow.is_default ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          <Star className="w-3.5 h-3.5" /> {workflow.is_default ? 'Default' : 'Set as default'}
        </button>
        <button onClick={onRemove} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {lineManagerStepCount > 1 && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          More than one "Line Manager" step resolves to the same person every time — for real multi-level
          chains, add specific people for the later steps instead.
        </div>
      )}

      <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
        {steps.length === 0 ? (
          <div className="p-4 text-center text-gray-400 text-sm">No steps yet — add one below</div>
        ) : (
          steps.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2 px-3 py-2">
              <span className="text-xs text-gray-400 w-5 flex-shrink-0">{s.step_order}.</span>
              <span className="flex-1 text-sm text-gray-900">{stepLabel(s)}</span>
              <button onClick={() => moveStep(s.id, 'up')} disabled={i === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                <ChevronUp className="w-4 h-4" />
              </button>
              <button onClick={() => moveStep(s.id, 'down')} disabled={i === steps.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                <ChevronDown className="w-4 h-4" />
              </button>
              <button onClick={() => removeStep(s.id)} className="p-1 text-gray-400 hover:text-red-600">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))
        )}
      </div>

      <form onSubmit={addStep} className="flex gap-2 flex-wrap items-center">
        <select className="input-field w-40 text-sm py-1.5" value={stepType} onChange={e => setStepType(e.target.value as StepType)}>
          <option value="line_manager">Line Manager</option>
          <option value="specific_user">Specific Person</option>
        </select>
        {stepType === 'specific_user' && (
          <select className="input-field w-52 text-sm py-1.5" value={specificUserId} onChange={e => setSpecificUserId(e.target.value)} required>
            <option value="">Choose a person...</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
          </select>
        )}
        <button type="submit" className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Add Step
        </button>
      </form>
    </div>
  );
}

function FinanceTab({ financeApprovers, users, currentBusiness, businessId, onChange }: {
  financeApprovers: FinanceApprover[]; users: User[]; currentBusiness: Business | null; businessId?: string; onChange: () => void;
}) {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [error, setError] = useState('');
  const [delegateId, setDelegateId] = useState(currentBusiness?.acting_ceo_user_id || '');
  const [delegateSaving, setDelegateSaving] = useState(false);
  const [delegateMsg, setDelegateMsg] = useState('');

  useEffect(() => { setDelegateId(currentBusiness?.acting_ceo_user_id || ''); }, [currentBusiness]);

  const addApprover = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!selectedUserId) return;
    try {
      await business.financeApprovers.add(selectedUserId, businessId);
      setSelectedUserId('');
      onChange();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const removeApprover = async (userId: string) => {
    if (!confirm('Remove this person as a finance approver?')) return;
    try {
      await business.financeApprovers.remove(userId, businessId);
      onChange();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const saveDelegate = async () => {
    setDelegateSaving(true);
    setDelegateMsg('');
    setError('');
    try {
      await business.setDelegate(delegateId || null, businessId);
      setDelegateMsg('Delegate updated');
      onChange();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDelegateSaving(false);
    }
  };

  const nonApprovers = users.filter(u => !financeApprovers.some(fa => fa.user_id === u.id));
  const delegateCandidates = users.filter(u => u.role !== 'ceo' && u.is_active);

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900 text-sm">Finance Approvers</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Anyone listed here can finance-approve and pay expenses once they've been approved by a line manager —
            regardless of their role or job title.
          </p>
        </div>
        <form onSubmit={addApprover} className="flex gap-2">
          <select className="input-field flex-1" value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}>
            <option value="">Choose a person...</option>
            {nonApprovers.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
          </select>
          <button type="submit" className="btn-primary flex items-center gap-2 flex-shrink-0" disabled={!selectedUserId}>
            <Plus className="w-4 h-4" /> Add
          </button>
        </form>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
        <div className="card overflow-hidden p-0">
          {financeApprovers.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">No finance approvers assigned yet</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {financeApprovers.map(fa => (
                <div key={fa.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{fa.full_name || fa.email}</div>
                    <div className="text-xs text-gray-400">{fa.email} · {ROLE_OPTIONS.find(r => r.value === fa.role)?.label || fa.role}</div>
                  </div>
                  <button onClick={() => removeApprover(fa.user_id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card space-y-3">
        <div>
          <h3 className="font-semibold text-gray-900 text-sm">CEO Delegate</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            While set, this person can act on any approval that would otherwise require the CEO — useful while the CEO is away.
            The CEO can still act themselves at any time.
          </p>
        </div>
        {delegateMsg && <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg text-xs">{delegateMsg}</div>}
        <div className="flex gap-2">
          <select className="input-field flex-1" value={delegateId} onChange={e => setDelegateId(e.target.value)}>
            <option value="">No delegate — CEO only</option>
            {delegateCandidates.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
          </select>
          <button onClick={saveDelegate} className="btn-primary flex-shrink-0" disabled={delegateSaving}>
            {delegateSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
